import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Query {
  id: string;
  question: string;
  upvotes: number;
  reuse_count: number;
  created_at: string;
}

export default function CommunityQuestionsSection() {
  const [queries, setQueries] = useState<Query[]>([]);
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'most-asked'>('popular');
  const [loading, setLoading] = useState(true);
  const [upvotedQueries, setUpvotedQueries] = useState<Set<string>>(new Set());
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
    fetchQueries();
    
    const interval = setInterval(fetchQueries, 30000);
    return () => clearInterval(interval);
  }, [sortBy]);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (user) {
        const { data } = await supabase
          .from('query_upvotes')
          .select('query_id')
          .eq('user_id', user.id);
        
        if (data) {
          setUpvotedQueries(new Set(data.map(u => u.query_id)));
        }
      }
    } catch (error) {
      console.error('Error checking user:', error);
    }
  };

  const fetchQueries = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-public-queries?sort=${sortBy}&limit=20`,
        {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
        }
      );
      const data = await response.json();
      
      if (data.error) {
        console.error('Error from API:', data.error);
        setQueries([]);
      } else {
        setQueries(data.queries || []);
      }
    } catch (error) {
      console.error('Error fetching queries:', error);
      setQueries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (queryId: string) => {
    if (!user) {
      alert('Please sign in to upvote questions');
      return;
    }

    const isUpvoted = upvotedQueries.has(queryId);
    const action = isUpvoted ? 'remove' : 'upvote';

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      console.log('Upvote attempt:', { queryId, action, hasSession: !!session, hasToken: !!session?.access_token });
      
      if (!session?.access_token) {
        console.error('No valid session found');
        alert('Your session has expired. Please sign in again.');
        return;
      }

      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/upvote-query`;
      console.log('Calling upvote function:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ queryId, action }),
      });

      console.log('Response status:', response.status);
      
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (response.ok && responseData.success) {
        console.log('Upvote successful, updating UI');
        setQueries(prev =>
          prev.map(q =>
            q.id === queryId
              ? { ...q, upvotes: q.upvotes + (isUpvoted ? -1 : 1) }
              : q
          )
        );

        setUpvotedQueries(prev => {
          const newSet = new Set(prev);
          if (isUpvoted) {
            newSet.delete(queryId);
          } else {
            newSet.add(queryId);
          }
          return newSet;
        });
      } else {
        console.error('Upvote failed:', responseData);
        throw new Error(responseData.error || 'Failed to upvote');
      }
    } catch (error) {
      console.error('Error upvoting:', error);
      alert(`Failed to upvote. ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  const handleQuestionClick = async (query: Query) => {
    // Track the reuse
    try {
      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/track-question-reuse`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ queryId: query.id }),
        }
      );
      
      // Update local state to reflect the reuse
      setQueries(prev =>
        prev.map(q =>
          q.id === query.id
            ? { ...q, reuse_count: (q.reuse_count || 0) + 1 }
            : q
        )
      );
    } catch (error) {
      console.error('Error tracking reuse:', error);
    }
    
    // Navigate to Oracle page with the question as a URL parameter
    navigate(`/oracle?q=${encodeURIComponent(query.question)}`);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Community Questions
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-lg text-gray-600 max-w-2xl mx-auto"
          >
            See what others are asking the Oracle. Upvote questions you find interesting!
          </motion.p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="flex justify-center gap-3 mb-12"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSortBy('popular')}
            className={`px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
              sortBy === 'popular'
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <i className="ri-fire-fill mr-2"></i>
            Most Popular
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSortBy('most-asked')}
            className={`px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
              sortBy === 'most-asked'
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <i className="ri-repeat-fill mr-2"></i>
            Most Asked
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSortBy('recent')}
            className={`px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
              sortBy === 'recent'
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <i className="ri-time-line mr-2"></i>
            Most Recent
          </motion.button>
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full"
            ></motion.div>
          </div>
        ) : queries.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100"
          >
            <motion.i 
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 10, -10, 0]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="ri-question-line text-6xl text-gray-300 mb-4"
            ></motion.i>
            <p className="text-gray-500 text-lg">No questions yet. Be the first to ask!</p>
          </motion.div>
        ) : (
          <div className="grid gap-4 max-w-4xl mx-auto">
            <AnimatePresence mode="popLayout">
              {queries.map((query, index) => (
                <motion.div
                  key={query.id}
                  layout
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ 
                    duration: 0.4,
                    delay: index * 0.05,
                    layout: { duration: 0.3 }
                  }}
                  whileHover={{ y: -4, scale: 1.01 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpvote(query.id);
                      }}
                      className={`flex flex-col items-center gap-1 min-w-[60px] py-2 px-3 rounded-lg transition-all cursor-pointer ${
                        upvotedQueries.has(query.id)
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <motion.i 
                        animate={upvotedQueries.has(query.id) ? { 
                          y: [0, -5, 0],
                          scale: [1, 1.2, 1]
                        } : {}}
                        transition={{ duration: 0.3 }}
                        className={`text-2xl ${upvotedQueries.has(query.id) ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'}`}
                      ></motion.i>
                      <motion.span 
                        key={query.upvotes}
                        initial={{ scale: 1.3, color: '#f59e0b' }}
                        animate={{ scale: 1, color: 'inherit' }}
                        className="font-bold text-sm"
                      >
                        {query.upvotes}
                      </motion.span>
                    </motion.button>

                    <div 
                      className="flex-1 min-w-0 cursor-pointer group"
                      onClick={() => handleQuestionClick(query)}
                    >
                      <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-gray-900 text-lg leading-relaxed mb-2 group-hover:text-amber-700 transition-colors"
                      >
                        {query.question}
                      </motion.p>
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1.5">
                            <i className="ri-time-line"></i>
                            <span>{formatTimeAgo(query.created_at)}</span>
                          </div>
                          {(query.reuse_count || 0) > 0 && (
                            <motion.div 
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-600 rounded-full"
                            >
                              <i className="ri-repeat-line text-sm"></i>
                              <span className="text-xs font-semibold">
                                {query.reuse_count} {query.reuse_count === 1 ? 'reuse' : 'reuses'}
                              </span>
                            </motion.div>
                          )}
                        </div>
                        <span className="text-xs text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                          <i className="ri-arrow-right-up-line"></i>
                          Ask this question
                        </span>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
}
